// ============================================================
// cf-notification.js � Notification Bell & Approval Badge
// Loaded on every page via _Layout.cshtml (after cf-signalr.js).
//
// Connects the notification bell dropdown and approval badge
// to backend APIs and SignalR real-time push events.
//
// Init flow (called by cf-auth.js after loadMe succeeds):
//   CF.notification.init()
//     ? GET /api/v1/user-notifications/unread       ? render bell items
//     ? GET /api/v1/user-notifications/unread/count  ? update bell badge
//
// SignalR event handlers (called by cf-signalr.js):
//   CF.notification.onReceived(payload)
//   CF.notification.onUnreadCountChanged(payload)
//   CF.notification.onApprovalReceived(payload)
//   CF.notification.onApprovalCountChanged(payload)
//   CF.notification.onApprovalDismissed(payload)
//
// Resync (called by cf-signalr.js after reconnect):
//   CF.notification.resync()  � re-fetches all notification state from API
//
// Public API:
//   CF.notification.init()         � fetch initial data from API
//   CF.notification.resync()       � re-fetch after SignalR reconnect
//   CF.notification.markAllRead()  � PATCH /api/v1/user-notifications/read-all
//   CF.notification.markRead(id)   � PATCH /api/v1/user-notifications/{id}/read
// ============================================================

window.CF = window.CF || {};

CF.notification = (function () {
    'use strict';

    var _initialized = false;

    var UNREAD_PATH       = '/api/v1/user-notifications/unread';
    var UNREAD_COUNT_PATH = '/api/v1/user-notifications/unread/count';
    var APPROVAL_PENDING_PATH = '/api/v1/user-notifications/approval/pending';
    var APPROVAL_COUNT_PATH = '/api/v1/user-notifications/approval/count';
    var READ_PATH         = '/api/v1/user-notifications/{id}/read';
    var READ_ALL_PATH     = '/api/v1/user-notifications/read-all';

    // ????????????????????????????????????????????
    // SEVERITY ? icon/color mapping
    // Matches NotificationSeverities constants
    // ????????????????????????????????????????????
    var SEVERITY_MAP = {
        'Info':    { iconCls: 'fa-solid fa-circle-info',    colorCls: 'blue'  },
        'Success': { iconCls: 'fa-solid fa-circle-check',   colorCls: 'green' },
        'Warning': { iconCls: 'fa-solid fa-triangle-exclamation', colorCls: 'amber' },
        'Error':   { iconCls: 'fa-solid fa-circle-xmark',   colorCls: 'red'   }
    };

    // ????????????????????????????????????????????
    // DOM HELPERS � InApp
    // ????????????????????????????????????????????
    function _badgeEl()    { return document.getElementById('notif-badge'); }
    function _dotEl()      { return document.getElementById('notif-dot'); }
    function _listEl()     { return document.getElementById('notif-list'); }
    function _countEl()    { return document.getElementById('notif-count-label'); }
    function _emptyEl()    { return document.getElementById('notif-empty'); }
    function _markAllEl()  { return document.getElementById('notif-mark-all'); }

    // ????????????????????????????????????????????
    // DOM HELPERS � Approval
    // ????????????????????????????????????????????
    function _approvalBadgeEl()  { return document.getElementById('approval-badge'); }
    function _approvalDotEl()    { return document.getElementById('approval-dot'); }
    function _approvalListEl()   { return document.getElementById('approval-list'); }
    function _approvalCountEl()  { return document.getElementById('approval-count-label'); }
    function _approvalEmptyEl()  { return document.getElementById('approval-empty'); }

    // ????????????????????????????????????????????
    // TIME AGO
    // ????????????????????????????????????????????
    function _timeAgo(dateStr) {
        var now  = Date.now();
        var then = new Date(dateStr).getTime();
        var diff = Math.max(0, now - then);
        var sec  = Math.floor(diff / 1000);
        var min  = Math.floor(sec / 60);
        var hr   = Math.floor(min / 60);
        var day  = Math.floor(hr / 24);

        if (sec < 60) return 'just now';
        if (min < 60) return min + (min === 1 ? ' minute ago' : ' minutes ago');
        if (hr  < 24) return hr  + (hr  === 1 ? ' hour ago'  : ' hours ago');
        if (day < 30) return day + (day === 1 ? ' day ago'   : ' days ago');
        return new Date(dateStr).toLocaleDateString();
    }

    // ????????????????????????????????????????????
    // RENDER: Update bell badge number + dot (InApp)
    // ????????????????????????????????????????????
    function _updateBadge(count) {
        var badge = _badgeEl();
        var dot   = _dotEl();
        var countLabel = _countEl();

        if (badge) {
            if (count > 0) {
                badge.textContent = count > 99 ? '99+' : count;
                badge.style.display = 'flex';
            } else {
                badge.style.display = 'none';
            }
        }

        if (dot) {
            dot.style.display = count > 0 ? 'block' : 'none';
        }

        if (countLabel) {
            countLabel.textContent = count > 0
                ? count + ' new'
                : 'No new';
        }

        // Show/hide mark all read button
        var markAll = _markAllEl();
        if (markAll) {
            markAll.style.display = count > 0 ? 'inline' : 'none';
        }
    }

    // ????????????????????????????????????????????
    // RENDER: Update approval badge number + dot
    // ????????????????????????????????????????????
    function _updateApprovalBadge(count) {
        var badge = _approvalBadgeEl();
        var dot   = _approvalDotEl();
        var countLabel = _approvalCountEl();

        if (badge) {
            if (count > 0) {
                badge.textContent = count > 99 ? '99+' : count;
                badge.style.display = 'flex';
            } else {
                badge.style.display = 'none';
            }
        }

        if (dot) {
            dot.style.display = count > 0 ? 'block' : 'none';
        }

        if (countLabel) {
            countLabel.textContent = count > 0
                ? count + ' pending'
                : 'No pending';
        }
    }

    // ????????????????????????????????????????????
    // RENDER: Build notification list HTML (InApp)
    // ????????????????????????????????????????????
    function _renderList(items) {
        var list  = _listEl();
        var empty = _emptyEl();
        if (!list) return;

        if (!items || items.length === 0) {
            list.innerHTML = '';
            if (empty) empty.style.display = 'block';
            return;
        }

        if (empty) empty.style.display = 'none';

        var html = '';
        for (var i = 0; i < items.length; i++) {
            html += _buildItemHtml(items[i], true);
        }
        list.innerHTML = html;
    }

    // ????????????????????????????????????????????
    // RENDER: Build approval list HTML
    // ????????????????????????????????????????????
    function _renderApprovalList(items) {
        var list  = _approvalListEl();
        var empty = _approvalEmptyEl();
        if (!list) return;

        if (!items || items.length === 0) {
            list.innerHTML = '';
            if (empty) empty.style.display = 'block';
            return;
        }

        if (empty) empty.style.display = 'none';

        var html = '';
        for (var i = 0; i < items.length; i++) {
            html += _buildApprovalItemHtml(items[i]);
        }
        list.innerHTML = html;
    }

    function _buildItemHtml(item, isUnread) {
        var sev = SEVERITY_MAP[item.severity] || SEVERITY_MAP['Info'];
        var iconHtml = item.icon
            ? '<i class="' + item.icon + '"></i>'
            : '<i class="' + sev.iconCls + '"></i>';

        return '<div class="notif-item' + (isUnread ? ' unread' : '') + '"' +
            ' data-notif-id="' + item.id + '"' +
            ' onclick="CF.notification.markRead(\'' + item.id + '\')">' +
            '<div class="notif-icon ' + sev.colorCls + '">' + iconHtml + '</div>' +
            '<div class="notif-content">' +
                '<div class="notif-content-title">' + _escHtml(item.title) + '</div>' +
                '<div class="notif-content-text">' + _escHtml(item.body) + '</div>' +
                '<div class="notif-content-time">' + _timeAgo(item.createdAt) + '</div>' +
            '</div>' +
            (isUnread ? '<div class="notif-unread-dot"></div>' : '') +
        '</div>';
    }

    function _buildApprovalItemHtml(item) {
        var sev = SEVERITY_MAP[item.severity] || SEVERITY_MAP['Warning'];
        var iconHtml = item.icon
            ? '<i class="' + item.icon + '"></i>'
            : '<i class="' + sev.iconCls + '"></i>';

        // referenceId = approval instance id (ReferenceType=ApprovalInstance) —
        // klik membawa approver langsung ke halaman keputusan.
        var clickAttr = item.referenceId
            ? ' onclick="CF.notification.openApproval(\'' + item.referenceId + '\')" style="cursor:pointer"'
            : '';

        return '<div class="notif-item unread"' +
            ' data-approval-id="' + item.id + '"' +
            ' data-reference-id="' + (item.referenceId || '') + '"' + clickAttr + '>' +
            '<div class="notif-icon purple">' + iconHtml + '</div>' +
            '<div class="notif-content">' +
                '<div class="notif-content-title">' + _escHtml(item.title) + '</div>' +
                '<div class="notif-content-text">' + _escHtml(item.body) + '</div>' +
                '<div class="notif-content-time">' + _timeAgo(item.createdAt) + '</div>' +
            '</div>' +
        '</div>';
    }

    // ????????????????????????????????????????????
    // RENDER: Prepend a single new item to the list (InApp)
    // ????????????????????????????????????????????
    function _prependItem(item) {
        var list  = _listEl();
        var empty = _emptyEl();
        if (!list) return;

        if (empty) empty.style.display = 'none';

        var temp = document.createElement('div');
        temp.innerHTML = _buildItemHtml(item, true);
        var newNode = temp.firstChild;

        if (list.firstChild) {
            list.insertBefore(newNode, list.firstChild);
        } else {
            list.appendChild(newNode);
        }

        // Keep max 10 items in dropdown
        while (list.children.length > 10) {
            list.removeChild(list.lastChild);
        }
    }

    // ????????????????????????????????????????????
    // RENDER: Prepend a single new approval item
    // ????????????????????????????????????????????
    function _prependApprovalItem(item) {
        var list  = _approvalListEl();
        var empty = _approvalEmptyEl();
        if (!list) return;

        if (empty) empty.style.display = 'none';

        var temp = document.createElement('div');
        temp.innerHTML = _buildApprovalItemHtml(item);
        var newNode = temp.firstChild;

        if (list.firstChild) {
            list.insertBefore(newNode, list.firstChild);
        } else {
            list.appendChild(newNode);
        }

        // Keep max 20 items in dropdown
        while (list.children.length > 20) {
            list.removeChild(list.lastChild);
        }
    }

    function _escHtml(str) {
        if (!str) return '';
        var div = document.createElement('div');
        div.appendChild(document.createTextNode(str));
        return div.innerHTML;
    }

    // ????????????????????????????????????????????
    // INTERNAL: Fetch notification state from API
    // Shared between init() and resync()
    // ????????????????????????????????????????????
    function _fetchNotificationState() {
        // Fetch unread items for dropdown
        CF.auth.api({ url: UNREAD_PATH, method: 'GET' }).then(
            function (res) {
                if (res.success && res.data) {
                    _renderList(res.data);
                }
            }
        );

        // Fetch unread count for badge
        CF.auth.api({ url: UNREAD_COUNT_PATH, method: 'GET' }).then(
            function (res) {
                if (res.success && typeof res.data === 'number') {
                    _updateBadge(res.data);
                }
            }
        );

        // Fetch approval pending items
        CF.auth.api({ url: APPROVAL_PENDING_PATH, method: 'GET' }).then(
            function (res) {
                if (res.success && res.data) {
                    _renderApprovalList(res.data);
                }
            }
        );

        // Fetch approval count for badge
        CF.auth.api({ url: APPROVAL_COUNT_PATH, method: 'GET' }).then(
            function (res) {
                if (res.success && typeof res.data === 'number') {
                    _updateApprovalBadge(res.data);
                }
            }
        );
    }

    // ????????????????????????????????????????????
    // PUBLIC: init() � fetch initial data (once per page load)
    // ????????????????????????????????????????????
    function init() {
        if (_initialized) return;
        _initialized = true;

        _fetchNotificationState();
    }

    // ????????????????????????????????????????????
    // PUBLIC: resync() � re-fetch after SignalR reconnect
    // Bypasses _initialized guard because we need fresh
    // data to catch notifications missed during disconnect.
    // ????????????????????????????????????????????
    function resync() {
        _fetchNotificationState();
    }

    // ????????????????????????????????????????????
    // API: Mark single notification as read
    // ????????????????????????????????????????????
    function markRead(notifId) {
        var url = READ_PATH.replace('{id}', notifId);

        CF.auth.api({ url: url, method: 'PATCH' }).then(
            function () {
                // Remove unread styling from the clicked item
                var item = document.querySelector('[data-notif-id="' + notifId + '"]');
                if (item) {
                    item.classList.remove('unread');
                    var dot = item.querySelector('.notif-unread-dot');
                    if (dot) dot.remove();
                    item.removeAttribute('onclick');
                }
            }
        );
        // Badge count will be updated via SignalR unread-count-changed event
    }

    // ????????????????????????????????????????????
    // API: Mark all as read
    // ????????????????????????????????????????????
    function markAllRead() {
        CF.auth.api({ url: READ_ALL_PATH, method: 'PATCH' }).then(
            function () {
                // Remove all unread styling
                var items = document.querySelectorAll('.notif-item.unread');
                for (var i = 0; i < items.length; i++) {
                    // Only clear in-app items (not approval items)
                    if (items[i].hasAttribute('data-notif-id')) {
                        items[i].classList.remove('unread');
                        var dot = items[i].querySelector('.notif-unread-dot');
                        if (dot) dot.remove();
                        items[i].removeAttribute('onclick');
                    }
                }
            }
        );
        // Approval items are handled by approval-related SignalR events
    }

    // ????????????????????????????????????????????
    // SignalR: New in-app notification received
    // ????????????????????????????????????????????
    function onReceived(payload) {
        // payload = NotificationItemResponse shape
        if (payload) {
            _prependItem(payload);

            // Show toast for the new notification
            var sev = (payload.severity || 'info').toLowerCase();
            var toastFn = CF.toast[sev] || CF.toast.info;
            toastFn(payload.body || payload.title, payload.title);
        }
    }

    // ????????????????????????????????????????????
    // SignalR: Unread count changed (after read/readAll)
    // ????????????????????????????????????????????
    function onUnreadCountChanged(payload) {
        // payload = { count: N }
        var count = (payload && typeof payload.count === 'number')
            ? payload.count
            : 0;
        _updateBadge(count);
    }

    // ????????????????????????????????????????????
    // SignalR: New approval notification received
    // ????????????????????????????????????????????
    function onApprovalReceived(payload) {
        if (payload) {
            _prependApprovalItem(payload);
            CF.toast.warning(payload.body || payload.title, payload.title || 'Approval Request');
        }
    }

    // ????????????????????????????????????????????
    // SignalR: Approval pending count changed
    // ????????????????????????????????????????????
    function onApprovalCountChanged(payload) {
        // payload = { count: N }
        var count = (payload && typeof payload.count === 'number')
            ? payload.count
            : 0;
        _updateApprovalBadge(count);
    }

    // ????????????????????????????????????????????
    // SignalR: Approval dismissed
    // ????????????????????????????????????????????
    function onApprovalDismissed(payload) {
        if (!payload || !payload.referenceId) return;

        var list = _approvalListEl();
        if (!list) return;

        var items = list.querySelectorAll('[data-reference-id="' + payload.referenceId + '"]');
        for (var i = 0; i < items.length; i++) {
            items[i].remove();
        }

        // Show empty state if no items left
        if (list.children.length === 0) {
            var empty = _approvalEmptyEl();
            if (empty) empty.style.display = 'block';
        }
    }

    // ????????????????????????????????????????????
    // NAV: Open approval item for decision
    // Redirect ke Task Approval Pending dengan deep-link ?open=
    // (pending.js membuka modal aksi / panel validasi otomatis).
    // Badge count berkurang setelah keputusan diambil — server
    // men-dismiss notifikasi dan push approval.pending-count-changed.
    // ????????????????????????????????????????????
    function openApproval(referenceId) {
        if (!referenceId) return;
        window.location.href = '/TaskApproval/Pending?open=' + encodeURIComponent(referenceId);
    }

    // ????????????????????????????????????????????
    // RESET: Called on logout
    // ????????????????????????????????????????????
    function reset() {
        _initialized = false;

        _updateBadge(0);
        var list = _listEl();
        if (list) list.innerHTML = '';
        var empty = _emptyEl();
        if (empty) empty.style.display = 'block';

        _updateApprovalBadge(0);
        var approvalList = _approvalListEl();
        if (approvalList) approvalList.innerHTML = '';
        var approvalEmpty = _approvalEmptyEl();
        if (approvalEmpty) approvalEmpty.style.display = 'block';
    }

    return {
        init:                    init,
        resync:                  resync,
        markRead:                markRead,
        markAllRead:             markAllRead,
        onReceived:              onReceived,
        onUnreadCountChanged:    onUnreadCountChanged,
        onApprovalReceived:      onApprovalReceived,
        onApprovalCountChanged:  onApprovalCountChanged,
        onApprovalDismissed:     onApprovalDismissed,
        openApproval:            openApproval,
        reset:                   reset
    };

})();
